import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { ModalShell } from "@/components/ui/modal-shell";
import { Button } from "@/components/ui/button";
import { brDayKey } from "@/lib/datetime";
import {
  LINK_DO_APP,
  buildDoAparelho,
  usePrintAgentDevices,
  useUltimaVersaoDoApp,
  versaoMenor,
  type BuildDoApp,
  type PrintAgentDevice,
} from "@/lib/printAgentDevices";

// Pop-up da tela inicial (Pedro, 08/10/2026, via sessão da Atendente): o app de impressão não se atualiza sozinho — e
// não vai, decisão dele —, então quem abre o painel num navegador do Windows é avisado quando um computador da loja
// está com versão velha. Celular e Mac não veem: lá não dá para instalar. O navegador não sabe o nome do computador,
// por isso o texto cita o que o app registrou. "Depois" (e o X) vale até o dia seguinte, por aparelho e versão.
const CHAVE_DEPOIS = "vnz-app-impressao-depois";

function navegadorWindows(): boolean {
  return typeof navigator !== "undefined" && /Windows NT/i.test(navigator.userAgent);
}

function lerAdiados(): Record<string, string> {
  try {
    const v = JSON.parse(localStorage.getItem(CHAVE_DEPOIS) || "{}");
    return v && typeof v === "object" ? (v as Record<string, string>) : {};
  } catch {
    return {};
  }
}

const versaoVisivel = (d: PrintAgentDevice) => (d.agentVersion ?? "").replace(/-win8$/i, "");

interface Pendente {
  d: PrintAgentDevice;
  build: BuildDoApp;
  chave: string;
}

export function AvisoAppDesatualizado() {
  const windows = useMemo(navegadorWindows, []);
  const { data: devices = [] } = usePrintAgentDevices({ enabled: windows, refetchInterval: false });
  const doWindows = devices.filter((d) => d.os === "win32" && d.agentVersion);
  const { data: ultima } = useUltimaVersaoDoApp("windows", windows && doWindows.some((d) => buildDoAparelho(d) === "windows"));
  const { data: ultimaWin8 } = useUltimaVersaoDoApp("win8", windows && doWindows.some((d) => buildDoAparelho(d) === "win8"));
  const [adiados, setAdiados] = useState(lerAdiados);
  const hoje = brDayKey(new Date());

  const pendentes: Pendente[] = doWindows.flatMap((d) => {
    const build = buildDoAparelho(d);
    const alvo = (build === "win8" ? ultimaWin8 : ultima)?.version;
    if (!alvo || !versaoMenor(d.agentVersion!, alvo)) return [];
    const chave = `${d.deviceId}|${d.agentVersion}`;
    return adiados[chave] === hoje ? [] : [{ d, build, chave }];
  });

  function adiar() {
    const deHoje = Object.fromEntries(Object.entries(adiados).filter(([, dia]) => dia === hoje));
    const novo = { ...deHoje, ...Object.fromEntries(pendentes.map((p) => [p.chave, hoje])) };
    try {
      localStorage.setItem(CHAVE_DEPOIS, JSON.stringify(novo));
    } catch {
      /* sem storage: some só nesta visita */
    }
    setAdiados(novo);
  }

  if (!windows || pendentes.length === 0) return null;

  const builds = new Set(pendentes.map((p) => p.build));
  const misturado = builds.size > 1;
  const baixar = (build: BuildDoApp, rotulo: string) => (
    <Button asChild className="cursor-pointer">
      {/* Fecha depois do clique: o download sai na aba nova, como no botão de Configurações. */}
      <a href={LINK_DO_APP[build]} target="_blank" rel="noopener noreferrer" onClick={() => setTimeout(adiar, 0)} data-testid={`baixar-app-${build}`}>
        <Download className="w-4 h-4 mr-1" />
        {rotulo}
      </a>
    </Button>
  );
  const unico = pendentes.length === 1 ? pendentes[0]! : null;

  return (
    <ModalShell
      open
      onOpenChange={(v) => {
        if (!v) adiar();
      }}
      title="Atualize o aplicativo de impressão"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={adiar} className="cursor-pointer" data-testid="app-desatualizado-depois">
            Depois
          </Button>
          {/* Misturado (raro): o rodapé leva o instalador normal e o do Windows 7/8 vai na linha do computador — três
              botões não cabem no rodapé. */}
          {baixar(builds.has("windows") ? "windows" : "win8", "Baixar versão nova")}
        </>
      }
    >
      <div data-testid="app-desatualizado" className="flex flex-col gap-3 text-sm">
        {unico ? (
          <p>
            O aplicativo de impressão {unico.d.hostname ? `do computador ${unico.d.hostname}` : "de um computador da loja"} está
            desatualizado (versão {versaoVisivel(unico.d)}). Baixe a versão nova e instale por cima.
          </p>
        ) : (
          <>
            <p>O aplicativo de impressão está desatualizado nestes computadores. Baixe a versão nova e instale por cima em cada um.</p>
            <ul className="flex flex-col gap-1 rounded-lg border border-border bg-muted/30 px-3 py-2">
              {pendentes.map((p) => (
                <li key={p.chave} className="flex flex-wrap gap-x-1.5">
                  <span className="font-medium">{p.d.hostname ?? "Computador sem nome"}</span>
                  <span className="text-muted-foreground">
                    versão {versaoVisivel(p.d)}
                    {misturado && p.build === "win8" ? " (Windows 7 ou 8)" : ""}
                  </span>
                  {misturado && p.build === "win8" && (
                    <a
                      href={LINK_DO_APP.win8}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="basis-full text-primary underline underline-offset-2"
                      data-testid="baixar-app-win8"
                    >
                      Baixar a versão para Windows 7 e 8
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
        <p className="text-muted-foreground">
          Se aparecer “O Windows protegeu o computador”, clique em Mais informações → Executar assim mesmo.
        </p>
      </div>
    </ModalShell>
  );
}
